// client/src/components/event/RaffleConfig.jsx
/**
 * =================================================================
 * Raffle Configuration Component
 * =================================================================
 * FEATURES:
 * 1. Set number of winners per combination
 * 2. Schedule start/end date and time
 * 3. Display shareable hyperlink (copy-pasteable)
 * 4. View current entries and statistics
 * 5. Manual winner drawing (for testing/admin)
 * =================================================================
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AuctionConfig.css"; // Reuse auction styles
import TimeSelect from '../TimeSelect';
import ActionPostButton from './ActionPostButton';
import { utcToLocal, localToUTC } from '../../utils/timezoneUtils';

const RaffleConfig = ({ tenantId, eventId, itemId, itemName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  // Raffle configuration state
  const [config, setConfig] = useState({
    name: itemName || "Raffle",
    winners_per_combo: 1,
    start_date: "",      // YYYY-MM-DD
    start_time: "09:00", // HH:MM
    end_date: "",        // YYYY-MM-DD
    end_time: "17:00",   // HH:MM
    status: "NEW",
    active: 1,
    allow_paid_entries: false,
    entry_sale_mode: 'paid_only',
    price_per_entry: 0,
    max_entries_per_purchase: 10,
  });

  const [tenantTimezone, setTenantTimezone] = useState('America/Chicago');
  // Tenant-level flag — gates whether the paid-entries panel is shown at all.
  const [tenantPaidEntriesEnabled, setTenantPaidEntriesEnabled] = useState(false);

  // Raffle statistics
  const [stats, setStats] = useState(null);
  const [shareLink, setShareLink] = useState("");
  const [entries, setEntries] = useState([]);
  const [winners, setWinners] = useState([]);
  const [showEntries, setShowEntries] = useState(false);
  const [showWinners, setShowWinners] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";

  useEffect(() => {
    loadRaffleConfig();
    loadTenantFlag();
  }, [tenantId, eventId, itemId]);

  // Independent fetch so the paid-entries panel is available even before
  // the raffle row exists (first-time setup).
  const loadTenantFlag = async () => {
    try {
      const res = await axios.get(`${baseUrl}/api/tenants/${tenantId}/profile`);
      if (res.data?.success) {
        setTenantPaidEntriesEnabled(!!res.data.tenant?.paid_raffle_entries_enabled);
      }
    } catch (err) {
      console.warn('Could not load tenant flag for paid raffle entries:', err?.message || err);
    }
  };

  /**
   * Load existing raffle configuration
   */

  const loadRaffleConfig = async () => {
    setLoading(true);
    setMessage({ text: "", type: "" });
    
    try {
      const response = await axios.get(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}`
      );

      if (response.data.success) {
        const raffle = response.data.raffle;
        
        // Convert UTC datetime to tenant timezone
        const start = utcToLocal(raffle.start_at, raffle.timezone || 'America/Chicago');
        const end = utcToLocal(raffle.end_at, raffle.timezone || 'America/Chicago');
        
        setConfig({
          name: raffle.name || itemName || "Raffle",
          winners_per_combo: raffle.winners_per_combo || 1,
          start_date: start.date,
          start_time: start.time,
          end_date: end.date,
          end_time: end.time,
          status: raffle.status || "NEW",
          active: raffle.active || 1,
          allow_paid_entries: !!raffle.allow_paid_entries,
          entry_sale_mode: raffle.entry_sale_mode || 'paid_only',
          price_per_entry: Number(raffle.price_per_entry) || 0,
          max_entries_per_purchase: Number(raffle.max_entries_per_purchase) || 10,
        });

        setTenantTimezone(raffle.timezone || 'America/Chicago');
        setTenantPaidEntriesEnabled(!!raffle.tenant_paid_raffle_entries_enabled);
        setStats(raffle.stats);
        setShareLink(raffle.shareLink);

        if (raffle.status === "COMPLETE") {
          loadWinners(false);
        }
      }
    } catch (err) {
      console.error("Error loading raffle config:", err);
      
      // FIXED: Properly handle 404 - it means no raffle exists yet
      if (err.response?.status === 404) {
        setMessage({
          text: "New raffle: fill in the form below, then save.",
          type: "info",
        });
      } else {
        setMessage({
          text: "Failed to load raffle configuration",
          type: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };


  /**
   * Load entries
   */
  const loadEntries = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/entries`
      );

      if (response.data.success) {
        setEntries(response.data.entries);
        setShowEntries(true);
      }
    } catch (err) {
      console.error("Error loading entries:", err);
      setMessage({
        text: "Failed to load raffle entries",
        type: "error",
      });
    }
  };

  /**
   * Load winners
   */
  // show=false fetches the list without popping the Winners dialog over the
  // config (used on load for a completed action; buttons pass a click event).
  const loadWinners = async (show = true) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/winners`
      );

      if (response.data.success) {
        setWinners(response.data.winners);
        if (show) setShowWinners(true);
      }
    } catch (err) {
      console.error("Error loading winners:", err);
      setMessage({
        text: "Failed to load winners",
        type: "error",
      });
    }
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Validate configuration
   */
  const validateConfig = () => {
    const errors = [];

    if (!config.name || config.name.trim().length === 0) {
      errors.push("Raffle name is required");
    }

    if (config.winners_per_combo < 1 || config.winners_per_combo > 100) {
      errors.push("Winners per combination must be between 1 and 100");
    }

    if (config.start_at && config.end_at) {
      const start = new Date(config.start_at);
      const end = new Date(config.end_at);
      if (end <= start) {
        errors.push("End date must be after start date");
      }
    }

    return errors;
  };

  /**
   * Save raffle configuration
   */
  const handleSave = async (e) => {
    e.preventDefault();

    const errors = validateConfig();
    if (errors.length > 0) {
      setMessage({ text: errors.join(". "), type: "error" });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      // Convert to UTC for backend
      const start_at_utc = localToUTC(config.start_date, config.start_time, tenantTimezone);
      const end_at_utc = localToUTC(config.end_date, config.end_time, tenantTimezone);

      const payload = {
        name: config.name,
        winners_per_combo: Number(config.winners_per_combo),
        start_at: start_at_utc,
        end_at: end_at_utc,
        timezone: tenantTimezone,
        status: config.status,
        active: config.active,
      };

      if (tenantPaidEntriesEnabled) {
        payload.allow_paid_entries = config.allow_paid_entries ? 1 : 0;
        if (config.allow_paid_entries) {
          payload.entry_sale_mode = config.entry_sale_mode;
          payload.price_per_entry = Number(config.price_per_entry) || 0;
          payload.max_entries_per_purchase = Number(config.max_entries_per_purchase) || 10;
        }
      }

      const response = await axios.post(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/config`,
        payload
      );

      if (response.data.success) {
        setMessage({
          text: "Raffle configuration saved successfully!",
          type: "success",
        });
        
        setShareLink(response.data.shareLink);
        
        setTimeout(() => {
          setMessage({ text: "", type: "" });
          loadRaffleConfig();
        }, 2000);
      }
    } catch (err) {
      console.error("Error saving raffle config:", err);
      setMessage({
        text: err.response?.data?.error || "Failed to save raffle configuration",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Copy share link to clipboard
   */
  const copyShareLink = () => {
    if (!shareLink) return;

    navigator.clipboard.writeText(shareLink).then(
      () => {
        setMessage({
          text: "Link copied to clipboard!",
          type: "success",
        });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      },
      (err) => {
        console.error("Failed to copy:", err);
        setMessage({
          text: "Failed to copy link",
          type: "error",
        });
      }
    );
  };

  /**
   * Manually trigger winner drawing
   */
  const handleDrawWinners = async () => {
    if (!window.confirm(`Are you sure you want to draw ${config.winners_per_combo} winner(s) per combination now?`)) {
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.post(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/draw`
      );

      if (response.data.success) {
        const { count, combinations } = response.data;
        setMessage({
          text: `Successfully selected ${count} winner(s) from ${combinations} combination(s)!`,
          type: "success",
        });

        // Reload to get updated status
        setTimeout(() => {
          loadRaffleConfig();
          loadWinners();
        }, 1000);
      }
    } catch (err) {
      console.error("Error drawing winners:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to draw winners",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // State for notification sending
  const [sendingNotifications, setSendingNotifications] = useState(false);

  /**
   * Send notification emails to winners, participants, and admin
   */
  const sendNotifications = async () => {
    if (!window.confirm("Send notification emails to all winners, participants, and admin?")) {
      return;
    }

    setSendingNotifications(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.post(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/notify`
      );

      if (response.data.success) {
        const { winners_notified, participants_notified, admin_notified } = response.data.stats;
        setMessage({
          text: `Notifications sent: ${winners_notified} winner(s), ${participants_notified} participant(s)${admin_notified ? ", admin" : ""}`,
          type: "success",
        });
        // Reload winners to refresh notification status
        loadWinners();
      }
    } catch (err) {
      console.error("Error sending notifications:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to send notifications",
        type: "error",
      });
    } finally {
      setSendingNotifications(false);
    }
  };

  /**
   * Delete raffle configuration
   */
  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this raffle? This cannot be undone.")) {
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.delete(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}`
      );

      if (response.data.success) {
        setMessage({
          text: "Raffle deleted successfully",
          type: "success",
        });

        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error deleting raffle:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to delete raffle",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Format attributes for display
   */
  const formatAttributes = (attributes) => {
    if (!attributes) return "N/A";
    
    if (typeof attributes === 'string') {
      try {
        attributes = JSON.parse(attributes);
      } catch {
        return attributes;
      }
    }
    
    return Object.entries(attributes)
      .map(([key, val]) => `${key}: ${val}`)
      .join(', ');
  };

  if (loading) {
    return (
      <div className="auction-config-container">
        <div className="loading">Loading raffle configuration...</div>
      </div>
    );
  }

  return (
    <div className="auction-config-container">
      <div className="auction-config-header">
        <h2>Raffle Configuration</h2>
        {onClose && (
          <button onClick={onClose} className="close-button">
            ×
          </button>
        )}
      </div>

      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {/* Share Link Display */}
      {shareLink && (
        <div className="share-link-section">
          <h3>📋 Shareable Link</h3>
          <p className="share-link-description">
            Share this link with participants to let them enter the raffle:
          </p>
          <div className="share-link-container">
            <div className="share-link-display">
              <a href={shareLink} target="_blank" rel="noopener noreferrer">
                {(config.name || itemName || "").replace(/\s+/g, "_")}
              </a>
            </div>
            <button
              onClick={copyShareLink}
              className="copy-link-button"
              title="Copy link to clipboard"
            >
              📋 Copy Link
            </button>
            <ActionPostButton
              tenantId={tenantId}
              eventId={eventId}
              itemId={itemId}
              actionType="raffle"
              config={config}
              itemName={itemName}
              link={shareLink}
            />
          </div>
        </div>
      )}

      {/* Statistics Display */}
      {stats && (
        <div className="auction-stats-section">
          <h3>📊 Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-label">Total Entries</div>
              <div className="stat-value">{stats.entry_count || 0}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Unique Combinations</div>
              <div className="stat-value">{stats.unique_combinations || 0}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Winners Per Combo</div>
              <div className="stat-value">{config.winners_per_combo}</div>
            </div>
          </div>
          <div className="stats-actions">
            <button onClick={loadEntries} className="view-entries-button">
              View All Entries
            </button>
            {config.status === "COMPLETE" && (
              <button onClick={loadWinners} className="view-winners-button">
                View Winners
              </button>
            )}
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <form onSubmit={handleSave} className="auction-config-form">
        <div className="form-group">
          <label htmlFor="name">
            Raffle Name / Item Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={config.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Enter raffle name"
            disabled={config.status === "COMPLETE"}
            required
          />
          <small className="form-hint">
            This will be displayed as the hyperlink text in the shareable link
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="winners_per_combo">
            Winners Per Combination <span className="required">*</span>
          </label>
          <input
            type="number"
            id="winners_per_combo"
            value={config.winners_per_combo}
            onChange={(e) => handleInputChange("winners_per_combo", e.target.value)}
            min="1"
            max="100"
            disabled={config.status === "COMPLETE"}
            required
          />
          <small className="form-hint">
            Number of winners to select for each unique attribute combination
          </small>
        </div>

        {tenantPaidEntriesEnabled && (
          <div className="form-group" style={{ borderTop: '1px solid #333', paddingTop: 16, marginTop: 16 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!config.allow_paid_entries}
                onChange={(e) => handleInputChange('allow_paid_entries', e.target.checked)}
                disabled={config.status === 'COMPLETE'}
                style={{ width: 20, height: 20, marginTop: 2, accentColor: '#ff6800' }}
              />
              <span>
                <strong>Sell raffle entries</strong>
                <small className="form-hint" style={{ display: 'block', marginTop: 4 }}>
                  Charge users to purchase entries via Stripe. Each ticket = one chance to win.
                </small>
              </span>
            </label>

            {config.allow_paid_entries && (
              <div style={{ marginTop: 16, paddingLeft: 30 }}>
                <div className="form-group">
                  <label>Sale mode</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="entry_sale_mode"
                        value="paid_only"
                        checked={config.entry_sale_mode === 'paid_only'}
                        onChange={(e) => handleInputChange('entry_sale_mode', e.target.value)}
                        disabled={config.status === 'COMPLETE'}
                        style={{ accentColor: '#ff6800', flexShrink: 0 }}
                      />
                      <span>Paid only</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="entry_sale_mode"
                        value="first_free"
                        checked={config.entry_sale_mode === 'first_free'}
                        onChange={(e) => handleInputChange('entry_sale_mode', e.target.value)}
                        disabled={config.status === 'COMPLETE'}
                        style={{ accentColor: '#ff6800', flexShrink: 0 }}
                      />
                      <span>First entry free, paid for additional</span>
                    </label>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ textAlign: 'center' }}>
                    <label htmlFor="price_per_entry">Price per entry (USD) <span className="required">*</span></label>
                    <input
                      type="number"
                      id="price_per_entry"
                      min="0.50"
                      step="0.01"
                      value={config.price_per_entry}
                      onChange={(e) => handleInputChange('price_per_entry', e.target.value)}
                      disabled={config.status === 'COMPLETE'}
                      required
                      style={{ textAlign: 'center' }}
                    />
                    <small className="form-hint">Minimum $0.50 (Stripe minimum).</small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="max_entries_per_purchase">Max entries per purchase</label>
                    <input
                      type="number"
                      id="max_entries_per_purchase"
                      min="1"
                      max="100"
                      step="1"
                      value={config.max_entries_per_purchase}
                      onChange={(e) => handleInputChange('max_entries_per_purchase', e.target.value)}
                      disabled={config.status === 'COMPLETE'}
                    />
                    <small className="form-hint">Cap on tickets per single checkout (1–100).</small>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="start_at">Start Date & Time</label>
            <div className="row">
              <div className="col-md-6 mb-2">
                <input
                  type="date"
                  className="form-control"
                  value={config.start_date}
                  onChange={(e) => handleInputChange("start_date", e.target.value)}
                  disabled={config.status === "COMPLETE"}
                />
              </div>
              <div className="col-md-6">
                <TimeSelect
                  value={config.start_time}
                  onChange={(time) => handleInputChange("start_time", time)}
                  tenantTimezone={tenantTimezone}
                  showTimezone={true}
                  disabled={config.status === "COMPLETE"}
                  id="raffle-start-time"
                />
              </div>
            </div>
            <small className="form-hint">
              When the raffle opens for entries
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="end_at">End Date & Time</label>
            <div className="row">
              <div className="col-md-6 mb-2">
                <input
                  type="date"
                  className="form-control"
                  value={config.end_date}
                  onChange={(e) => handleInputChange("end_date", e.target.value)}
                  disabled={config.status === "COMPLETE"}
                />
              </div>
              <div className="col-md-6">
                <TimeSelect
                  value={config.end_time}
                  onChange={(time) => handleInputChange("end_time", time)}
                  tenantTimezone={tenantTimezone}
                  showTimezone={true}
                  disabled={config.status === "COMPLETE"}
                  id="raffle-end-time"
                />
              </div>
            </div>
            <small className="form-hint">
              When the raffle closes
            </small>
          </div>
        </div>

        <div className="form-actions">
          {config.status !== "COMPLETE" && (
            <>
              <button
                type="submit"
                className="save-button"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Configuration"}
              </button>
              {shareLink && (
                <button
                  type="button"
                  onClick={handleDrawWinners}
                  className="award-button"
                  disabled={saving}
                >
                  Draw Winners Now
                </button>
              )}
              {shareLink && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="delete-button"
                  disabled={saving}
                >
                  Delete Raffle
                </button>
              )}
            </>
          )}
          {config.status === "COMPLETE" && (
            <div className="completion-notice">
              ✅ This raffle has been completed and winners have been drawn
            </div>
          )}
        </div>
      </form>

      {/* Entries Modal */}
      {showEntries && (
        <div className="modal-overlay" onClick={() => setShowEntries(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>All Entries ({entries.length})</h3>
              <button onClick={() => setShowEntries(false)} className="close-button">
                ×
              </button>
            </div>
            <div className="modal-body">
              <table className="entries-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Selection</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, index) => (
                    <tr key={entry.id}>
                      <td data-label="#">{index + 1}</td>
                      <td data-label="Name">{entry.name}</td>
                      <td data-label="Email">{entry.email}</td>
                      <td data-label="Selection">{formatAttributes(entry.attributes)}</td>
                      <td data-label="Date">{new Date(entry.created_on).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Winners Modal */}
      {showWinners && (
        <div className="modal-overlay" onClick={() => setShowWinners(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🏆 Winners ({winners.length})</h3>
              <button onClick={() => setShowWinners(false)} className="close-button">
                ×
              </button>
            </div>
            <div className="modal-body">
              <table className="winners-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Selection</th>
                    <th>Email Status</th>
                    <th>Selected On</th>
                  </tr>
                </thead>
                <tbody>
                  {winners.map((winner, index) => (
                    <tr key={winner.id}>
                      <td className="place-cell" data-label="#">
                        {index + 1 <= 3 ?
                          (index === 0 ? "🥇" : index === 1 ? "🥈" : "🥉")
                          : index + 1}
                      </td>
                      <td data-label="Name">{winner.name}</td>
                      <td data-label="Email">{winner.email}</td>
                      <td data-label="Selection">{formatAttributes(winner.attributes)}</td>
                      <td data-label="Email Status">
                        <span className={`email-status ${winner.winner_notified ? 'sent' : 'pending'}`}>
                          {winner.winner_notified ? '✅ Sent' : '⏳ Pending'}
                        </span>
                      </td>
                      <td data-label="Selected On">{new Date(winner.created_on).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="notify-actions">
                <button
                  onClick={sendNotifications}
                  disabled={sendingNotifications}
                  className="send-notifications-button"
                >
                  {sendingNotifications ? "Sending..." : "Send Notification Emails"}
                </button>
                <small className="notify-hint">
                  Sends emails to all winners, participants, and admin
                </small>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RaffleConfig;